/**
 * Pure helpers for the Desk Saturation matrix.
 *
 * A mapping is one location-to-desk pair, identified by `deskId` + `locationId`. The persisted
 * state returned by `GET /desks/gates` is the baseline; the user's edits are kept as a sparse map
 * of pending changes on top of it, so that saving submits only the mappings that actually differ.
 */

// Shared by every "no mapping here" read, so it must stay immutable.
export const DISABLED_MAPPING = Object.freeze({ enabled: false, allGates: false, gateIds: Object.freeze([]) });

export const mappingKey = (deskId, locationId) => `${deskId}|${locationId}`;

const sortedUniqueIds = (gateIds) => [...new Set(gateIds)].sort((a, b) => a - b);

/**
 * Brings a mapping into its canonical form: `{ enabled, allGates, gateIds }`, with the gate ids
 * deduplicated and sorted. A mapping that covers every lane of its location is promoted to
 * `allGates`, the same way the backend stores it, so the comparison against the baseline is not
 * fooled by "all lanes ticked one by one".
 *
 * @param {object} mapping desired state of the mapping
 * @param {number[]} [locationGateIds] every lane of the location, used for the allGates promotion
 */
export const normalizeMapping = (mapping, locationGateIds) => {
    if (!mapping) return DISABLED_MAPPING;

    const gateIds = sortedUniqueIds(mapping.gateIds || []);
    const coversEveryLane = !!locationGateIds?.length && locationGateIds.every((gateId) => gateIds.includes(gateId));
    const allGates = Boolean(mapping.allGates) || coversEveryLane;

    return {
        enabled: allGates || gateIds.length > 0,
        allGates,
        gateIds: allGates ? [] : gateIds,
    };
};

export const isSameMapping = (mapping, otherMapping) => {
    const current = normalizeMapping(mapping);
    const other = normalizeMapping(otherMapping);

    return (
        current.enabled === other.enabled &&
        current.allGates === other.allGates &&
        current.gateIds.length === other.gateIds.length &&
        current.gateIds.every((gateId, index) => gateId === other.gateIds[index])
    );
};

const toMapping = (savedLocation, locationGateIds) =>
    normalizeMapping(
        {
            allGates: savedLocation.allGates,
            gateIds: (savedLocation.gates || []).map((gate) => gate.id),
        },
        locationGateIds
    );

/**
 * Persisted state of a single mapping, as last returned by the backend.
 *
 * `locationGateIds` is optional but should be passed whenever the lanes of the location are known:
 * it lets a persisted "every lane listed one by one" record normalize to `allGates` exactly like the
 * user's edits do, so the two are not reported as different.
 */
export const getBaselineMapping = (savedDesks, deskId, locationId, locationGateIds) => {
    const savedDesk = (savedDesks || []).find((desk) => desk.id === deskId);
    const savedLocation = savedDesk?.locations?.find((location) => location.id === locationId);

    return savedLocation ? toMapping(savedLocation, locationGateIds) : DISABLED_MAPPING;
};

/**
 * Lane ids of every known location, keyed by location id, for the `allGates` normalization above.
 */
export const buildLocationGateIdsMap = (locations = []) =>
    locations.reduce((byLocation, location) => {
        byLocation[location.id] = (location.gates || []).map((gate) => gate.id);
        return byLocation;
    }, {});

/**
 * Persisted state of the whole matrix, keyed by `mappingKey`. Mappings that are absent are
 * disabled, so a missing key reads as {@link DISABLED_MAPPING}.
 */
export const buildBaselineMap = (savedDesks = [], locations = []) => {
    const gateIdsByLocation = buildLocationGateIdsMap(locations);

    return savedDesks.reduce((baseline, desk) => {
        (desk.locations || []).forEach((location) => {
            baseline[mappingKey(desk.id, location.id)] = toMapping(location, gateIdsByLocation[location.id]);
        });
        return baseline;
    }, {});
};

/**
 * State the user currently sees: the baseline with the pending changes laid over it.
 */
export const applyPendingChanges = (baseline = {}, pendingChanges = {}) =>
    Object.entries(pendingChanges).reduce(
        (effective, [key, mapping]) => {
            effective[key] = normalizeMapping(mapping);
            return effective;
        },
        { ...baseline }
    );

export const getEffectiveMapping = (effectiveMappings, deskId, locationId) => effectiveMappings?.[mappingKey(deskId, locationId)] || DISABLED_MAPPING;

/**
 * Persisted state after a successful save: the backend stores exactly what was sent, so the
 * submitted changes are applied to the baseline desks. An enabled mapping is written as sent (an
 * all-gates mapping stays `allGates` rather than being expanded into every lane), a disabled one is
 * removed. A desk the baseline does not know yet is appended. The input is not mutated.
 */
export const applySavedChangesToBaseline = (savedDesks = [], submittedChanges = {}) => {
    const desks = savedDesks.map((desk) => ({ ...desk, locations: [...(desk.locations || [])] }));

    Object.values(submittedChanges).forEach(({ deskId, deskName, locationId, locationName, enabled, allGates, gateIds }) => {
        let desk = desks.find((savedDesk) => savedDesk.id === deskId);
        if (!desk) {
            if (!enabled) return;
            desk = { id: deskId, name: deskName, locations: [] };
            desks.push(desk);
        }

        desk.locations = desk.locations.filter((location) => location.id !== locationId);
        if (enabled) {
            desk.locations.push({
                id: locationId,
                name: locationName,
                allGates,
                gates: allGates ? [] : gateIds.map((gateId) => ({ id: gateId })),
            });
        }
    });

    return desks;
};

export const hasSaturationChanges = (pendingChanges) => Object.keys(pendingChanges || {}).length > 0;

/**
 * Turns the pending changes into the `PATCH /desks/gates` body: only the changed desks, and inside
 * each desk only its changed locations. `enabled: false` tells the backend to drop that mapping.
 */
export const buildSaturationChangePayload = (pendingChanges = {}) => {
    const changedDesks = new Map();

    Object.values(pendingChanges).forEach(({ deskId, deskName, locationId, locationName, enabled, allGates, gateIds }) => {
        if (!changedDesks.has(deskId)) {
            changedDesks.set(deskId, { id: deskId, name: deskName, locations: [] });
        }

        changedDesks.get(deskId).locations.push({
            id: locationId,
            name: locationName,
            enabled,
            allGates: enabled && allGates,
            gates: enabled && !allGates ? gateIds.map((gateId) => ({ id: gateId })) : [],
        });
    });

    return [...changedDesks.values()];
};
